'use client';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Rocket,
  TrendingUp,
  Crown,
  Lightbulb,
  ArrowRight,
  Shield,
  X,
  Sparkles,
  Clock,
  Users,
  ChevronRight,
  Lock,
} from 'lucide-react';

// Animated gradient mesh background
function GradientMesh() {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Base gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 80% 50% at 20% 30%, rgba(147, 51, 234, 0.15) 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 80% 70%, rgba(139, 92, 246, 0.12) 0%, transparent 50%),
            radial-gradient(ellipse 50% 30% at 50% 50%, rgba(192, 132, 252, 0.1) 0%, transparent 50%),
            linear-gradient(180deg, hsl(270 60% 99%) 0%, hsl(270 50% 97%) 100%)
          `,
        }}
      />

      {/* Animated orbs */}
      <div
        className="absolute w-[500px] h-[500px] rounded-full opacity-30 animate-float-slow"
        style={{
          top: '10%',
          left: '5%',
          background:
            'radial-gradient(circle, rgba(147, 51, 234, 0.2) 0%, transparent 70%)',
        }}
      />
      <div
        className="absolute w-[400px] h-[400px] rounded-full opacity-20 animate-float"
        style={{
          bottom: '10%',
          right: '10%',
          background:
            'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, transparent 70%)',
          animationDelay: '1s',
        }}
      />

      {/* Decorative grid */}
      <div
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `
            linear-gradient(hsl(271 81% 56%) 1px, transparent 1px),
            linear-gradient(90deg, hsl(271 81% 56%) 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px',
        }}
      />
    </div>
  );
}

// Typewriter effect component
function TypewriterText({
  text,
  isVisible,
}: {
  text: string;
  isVisible: boolean;
}) {
  const [displayText, setDisplayText] = useState('');
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    if (!isVisible) return;

    let index = 0;
    const interval = setInterval(() => {
      if (index <= text.length) {
        setDisplayText(text.slice(0, index));
        index++;
      } else {
        clearInterval(interval);
        // Hide cursor after typing completes
        setTimeout(() => setShowCursor(false), 1000);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [isVisible, text]);

  return (
    <span>
      {displayText}
      <span
        className={cn(
          'inline-block w-[3px] h-[1em] bg-purple-600 ml-1 align-middle transition-opacity',
          showCursor ? 'animate-pulse' : 'opacity-0'
        )}
      />
    </span>
  );
}

// Live activity ticker
function ActivityTicker({ isVisible }: { isVisible: boolean }) {
  const activities = [
    {
      name: 'Sarah K.',
      location: 'San Francisco',
      role: 'Founder',
      time: '2m ago',
    },
    {
      name: 'Michael T.',
      location: 'New York',
      role: 'Investor',
      time: '5m ago',
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % activities.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isVisible, activities.length]);

  const activity = activities[currentIndex];

  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-gray-100 shadow-sm transition-all duration-500',
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      )}
    >
      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
      <span className="text-sm text-gray-600">
        <span className="font-medium text-gray-900">{activity.name}</span>
        {' from '}
        {activity.location}
        {' applied as '}
        <span className="font-medium text-purple-600">{activity.role}</span>
      </span>
      <span className="text-xs text-gray-400">{activity.time}</span>
    </div>
  );
}

// Role selection modal/drawer
interface RoleOption {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  gradient: string;
  description: string;
}

const roleOptions: RoleOption[] = [
  {
    id: 'founder',
    title: 'Founder',
    subtitle: 'Build something fundable',
    icon: <Rocket className="w-6 h-6" />,
    gradient: 'from-purple-600 to-violet-600',
    description: 'Get clarity, support, and investor access',
  },
  {
    id: 'investor',
    title: 'Investor',
    subtitle: 'Invest with confidence',
    icon: <TrendingUp className="w-6 h-6" />,
    gradient: 'from-amber-500 to-orange-500',
    description: 'Curated deals with diligence support',
  },
];

function RoleSelector({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={cn(
          'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white rounded-2xl shadow-2xl z-50 transition-all duration-500',
          isOpen
            ? 'opacity-100 scale-100'
            : 'opacity-0 scale-95 pointer-events-none'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h3 className="font-display font-semibold text-gray-900 text-xl">
              Choose Your Role
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Select how you want to participate in the ecosystem
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Role options */}
        <div className="p-6 space-y-3">
          {roleOptions.map((role) => (
            <button
              key={role.id}
              onClick={() => setSelectedRole(role.id)}
              className={cn(
                'w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-300 text-left',
                selectedRole === role.id
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-100 hover:border-purple-200 hover:bg-gray-50'
              )}
            >
              <div
                className={cn(
                  'w-12 h-12 rounded-xl flex items-center justify-center text-white bg-gradient-to-br',
                  role.gradient
                )}
              >
                {role.icon}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-gray-900">{role.title}</h4>
                  <ChevronRight
                    className={cn(
                      'w-5 h-5 transition-all',
                      selectedRole === role.id
                        ? 'text-purple-600 translate-x-0'
                        : 'text-gray-300 -translate-x-1'
                    )}
                  />
                </div>
                <p className="text-sm text-gray-500">{role.description}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="p-6 pt-0">
          <Button
            onClick={() => {
              if (selectedRole) {
                const routes = {
                  founder: '/apply/founder',
                  investor: '/apply/investor',
                };
                window.location.href =
                  routes[selectedRole as keyof typeof routes];
              }
            }}
            className={cn(
              'w-full py-6 text-base rounded-xl transition-all',
              selectedRole
                ? 'bg-gradient-to-r from-purple-600 to-violet-600 text-white shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            )}
            disabled={!selectedRole}
          >
            <span className="flex items-center justify-center gap-2">
              Continue Application
              <ArrowRight className="w-4 h-4" />
            </span>
          </Button>
        </div>
      </div>
    </>
  );
}

// FAQ Accordion
function FAQItem({
  question,
  answer,
  isOpen,
  onToggle,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 text-left"
      >
        <span className="font-medium text-gray-900">{question}</span>
        <ChevronRight
          className={cn(
            'w-5 h-5 text-gray-400 transition-transform',
            isOpen && 'rotate-90'
          )}
        />
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-300',
          isOpen ? 'max-h-40 pb-4' : 'max-h-0'
        )}
      >
        <p className="text-gray-600 text-sm leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

export default function CTASection() {
  const [isVisible, setIsVisible] = useState(false);
  const [isRoleSelectorOpen, setIsRoleSelectorOpen] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const faqs = [
    {
      question: 'How long does the application process take?',
      answer:
        'Most applications are reviewed within 48 hours. The verification process may take 3-5 business days depending on your role and credentials.',
    },
    {
      question: 'Is there a cost to apply?',
      answer:
        'Applying is free. Platform fees only apply once you start engaging with deals or projects within the ecosystem.',
    },
    {
      question: 'Can I apply for multiple roles?',
      answer:
        'You can only hold one primary role, but some participants may qualify for secondary capabilities based on their credentials.',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setIsVisible(true);
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section
        id="apply"
        ref={sectionRef}
        className="relative py-16 lg:py-20 overflow-hidden"
      >
        <GradientMesh />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Activity ticker */}
          <div className="flex justify-center mb-8">
            <ActivityTicker isVisible={isVisible} />
          </div>

          {/* Main content */}
          <div className="text-center">
            {/* Headline with typewriter */}
            <div
              className={cn(
                'mb-6 transition-all duration-700',
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              )}
            >
              <h2
                className="font-display font-semibold text-gray-900"
                style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}
              >
                <TypewriterText text="Your Next Move" isVisible={isVisible} />
              </h2>
            </div>

            {/* Subheadline */}
            <p
              className={cn(
                'text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed transition-all duration-700 delay-300',
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              )}
            >
              This isn't open enrollment. It's a Structured Startup–Investor Matchmaking
              platform for serious participants ready to deliver and receive measurable
              outcomes.
            </p>

            {/* Exclusivity indicator */}
            <div
              className={cn(
                'flex flex-wrap justify-center gap-4 mb-10 transition-all duration-700 delay-400',
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              )}
            >
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-violet-50 border border-violet-200">
                <Lock className="w-4 h-4 text-violet-600" />
                <span className="text-sm font-medium text-violet-700">
                  Invite code required
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-100">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium text-amber-700">
                  Admin-reviewed applications
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-50 border border-purple-100">
                <Clock className="w-4 h-4 text-purple-500" />
                <span className="text-sm font-medium text-purple-700">
                  48-hour review guarantee
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-50 border border-green-100">
                <Shield className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">
                  Verified & secure
                </span>
              </div>
            </div>

            {/* Main CTA button */}
            <div
              className={cn(
                'mb-12 transition-all duration-700 delay-500',
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              )}
            >
              <Button
                size="lg"
                onClick={() => setIsRoleSelectorOpen(true)}
                className="btn-premium text-white px-12 py-7 text-lg rounded-2xl shadow-2xl shadow-purple-500/30 hover:shadow-purple-500/40 transition-all group"
              >
                <span className="flex items-center gap-3">
                  Apply for Access
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
            </div>

            {/* Stats row */}
            <div
              className={cn(
                'flex justify-center gap-8 lg:gap-16 mb-16 transition-all duration-700 delay-600',
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              )}
            >
              <div className="text-center">
                <p className="text-3xl font-display font-bold text-gray-900">
                  200+
                </p>
                <p className="text-sm text-gray-500">Active Members</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-display font-bold text-gray-900">
                  $50M+
                </p>
                <p className="text-sm text-gray-500">Deals Facilitated</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-display font-bold text-gray-900">
                  85%
                </p>
                <p className="text-sm text-gray-500">Success Rate</p>
              </div>
            </div>

            {/* Mini FAQ */}
            <div
              className={cn(
                'max-w-xl mx-auto bg-white rounded-2xl border border-gray-100 shadow-sm p-6 transition-all duration-700 delay-700',
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              )}
            >
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-4 h-4 text-purple-500" />
                Quick Questions
              </h3>
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openFAQ === index}
                  onToggle={() => setOpenFAQ(openFAQ === index ? null : index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Role selector modal */}
      <RoleSelector
        isOpen={isRoleSelectorOpen}
        onClose={() => setIsRoleSelectorOpen(false)}
      />
    </>
  );
}
